/**
 * @fileoverview MolJSON export.
 *
 * MolJSON is a minimal molecular representation schema introduced in
 * "Molecular Representations for Large Language Models" (Runcie, Imrie & Deane,
 * arXiv:2605.01822, May 2026; reference implementation github.com/oxpig/MolJSON).
 *
 * Shape:
 *   {
 *     atoms:        [{ id, element }],
 *     bonds:        [{ source, target, order }],   order in [0, 1, 1.5, 2, 3]
 *     charges:      [{ atom_id, formal_charge }] | null,   sparse, non-zero only
 *     aromatic_n_h: [{ atom_id, hcount }] | null,          sparse, aromatic N with explicit H
 *   }
 *
 * Hydrogens are IMPLICIT in MolJSON, inferred from the graph by valence rules. The one
 * case valence cannot infer is an aromatic nitrogen bearing hydrogen (pyrrole-type N vs
 * pyridine-type N), which is why the schema carries `aromatic_n_h` separately.
 *
 * MolJSON represents NEITHER 3D coordinates NOR stereochemistry. That is a property of
 * the format, not of this converter -- see `moljson-export-surface`, which requires the
 * loss to be disclosed at the point of export.
 *
 * WHY TWO SOURCES: BMaps molblocks are KEKULIZED. The bfd-server's MolWriter
 * (bioleap/bfd/writefile.elp) emits only bond types 1/2/3 -- never type 4 -- so
 * aromaticity cannot be recovered from the molblock text. It is carried separately on
 * the wire and lives on the in-memory bond (`Bond.orderOriginal` 8/9 aromatic, 7
 * delocalized; see decode.js `decodeBondType`). Conversely, per-atom FORMAL charge
 * exists only in the molblock's `ccc` column -- the client model carries formal charge
 * only per residue/molecule. So the molblock supplies atoms, bonds and charges, and the
 * model supplies aromaticity.
 */

import { getMolAtomLines } from './mol_format_utils';
import { isAromaticBondOrder } from './chem_utils';
import { ElementInfo } from '../chemistry/ElementInfo';

/** Header lines before the counts line in a V2000 molblock. */
const MolHeaderLines = 3;

/** MolJSON's symbol for a dummy atom -- the schema's escape hatch for "not an element". */
const DummyAtomSymbol = '*';

/**
 * MolJSON's `element` is an enumeration (H..Og, plus `*`), so anything outside it makes
 * the document fail schema validation. Server-written molblocks name the element from the
 * atomic number and are safe, but a user-imported SDF can carry `R`, `D`, `*` or another
 * placeholder, so map anything unrecognised onto the dummy symbol the schema provides.
 * @param {string} symbol
 * @returns {string} a valid MolJSON element symbol
 */
function normalizeElement(symbol) {
    const element = String(symbol || '').trim();
    if (element === DummyAtomSymbol) return element;
    return ElementInfo[element] ? element : DummyAtomSymbol;
}

/**
 * V2000 atom-block charge column (`ccc`) is a CODE, not a value.
 * Matches the encoding the bfd-server writes in writefile.elp:
 *     chargeCode = atchg < 0 ? abs(atchg)+4 : (atchg > 0 ? max(0, 4-atchg) : 0)
 * 4 is "doublet radical", which carries no formal charge.
 */
const ChargeCodeToFormalCharge = {
    0: 0, 1: 3, 2: 2, 3: 1, 4: 0, 5: -1, 6: -2, 7: -3,
};

/** MolJSON's permitted bond orders. */
const MolJsonBondOrders = [0, 1, 1.5, 2, 3];

/** V2000 bond block type -> MolJSON order. 4 is aromatic; 5-8 are query types. */
const MolBondTypeToOrder = {
    1: 1, 2: 2, 3: 3, 4: 1.5,
};

/**
 * Formats produced entirely on the client. The bfd-server's export goes through
 * OpenBabel, which has no MolJSON writer, so sending one of these to
 * `cmdExportSelection` would be an unknown format string on the wire.
 */
export const ClientOnlyExportFormats = ['moljson'];

/**
 * @param {string} format
 * @returns {boolean} true when the format must be produced on the client
 */
export function isClientOnlyExportFormat(format) {
    return ClientOnlyExportFormats.includes(String(format || '').toLowerCase());
}

/**
 * Normalized key for an unordered atom-index pair, so a bond can be looked up from
 * either direction.
 * @param {number} a 1-based atom index
 * @param {number} b 1-based atom index
 * @returns {string}
 */
export function bondKey(a, b) {
    return a < b ? `${a}-${b}` : `${b}-${a}`;
}

/**
 * Parse the counts line of a V2000 molblock.
 * @param {string[]} lines
 * @returns {{ nAtoms: number, nBonds: number }}
 * @throws {Error} if the counts line is missing or unparseable
 */
function parseCountsLine(lines) {
    const countsLine = lines[MolHeaderLines];
    if (countsLine == null) throw new Error('molblock has no counts line');
    if (countsLine.includes('V3000')) {
        // V3000 uses a completely different block structure; mis-parsing it as V2000
        // would silently produce nonsense. The repo carries V3000 SDFs in testdata.
        throw new Error('V3000 molblocks are not supported by the MolJSON converter');
    }

    // ⚠️ Do NOT lean on Number() alone here: `Number('')` is 0, so a blank or missing
    // counts line parses as a perfectly valid EMPTY molecule and arbitrary garbage
    // converts to `{atoms: [], bonds: []}` -- schema-valid output that misrepresents
    // its input, which is the one failure this converter must not have.
    const countField = (from, to) => {
        const text = countsLine.slice(from, to).trim();
        return /^\d+$/.test(text) ? Number(text) : null;
    };
    const nAtoms = countField(0, 3);
    const nBonds = countField(3, 6);
    if (nAtoms == null || nBonds == null || nAtoms < 1) {
        throw new Error(`molblock counts line is unparseable: "${countsLine}"`);
    }
    return { nAtoms, nBonds };
}

/**
 * Extract the formal charge from a parsed atom line's number fields.
 * `first6numbers` is the fixed-width run `dd ccc sss hhh bbb vvv`, so the charge code
 * `ccc` is characters 2-5.
 * @param {{ first6numbers: string? }} atomLine
 * @returns {number} formal charge (0 when absent or unrecognized)
 */
function formalChargeFromAtomLine(atomLine) {
    const first6 = atomLine.first6numbers;
    if (!first6) return 0;
    // getMolAtomLines() trims its captures, so re-pad to keep the fixed-width offsets.
    const padded = first6.padStart(17, ' ');
    const code = Number(padded.slice(2, 5).trim() || '0');
    return ChargeCodeToFormalCharge[code] ?? 0;
}

/**
 * Parse `M  CHG` property lines. When ANY are present the V2000 spec says the
 * atom-block charge column is void for the whole molecule.
 *
 * BMaps' own molblocks never carry these (the bfd-server writes the `ccc` column
 * instead), but a compound loaded from a user-supplied SDF can, so honour them.
 * Format: `M  CHGnn8 aaa vvv aaa vvv ...` where nn8 is the entry count.
 * @param {string[]} lines
 * @returns {Map<number, number>|null} 1-based atom index -> formal charge, or null if absent
 */
function parseChargeProperties(lines) {
    const chargeLines = lines.filter((line) => line.startsWith('M  CHG'));
    if (chargeLines.length === 0) return null;

    const charges = new Map();
    for (const line of chargeLines) {
        // Skip the `M  CHG` tag and the entry count, then read index/value pairs.
        const fields = line.slice(6).trim().split(/\s+/).map(Number);
        const [, ...pairs] = fields;
        for (let i = 0; i + 1 < pairs.length; i += 2) {
            const [atomIndex, charge] = [pairs[i], pairs[i + 1]];
            if (Number.isInteger(atomIndex) && Number.isInteger(charge)) {
                charges.set(atomIndex, charge);
            }
        }
    }
    return charges;
}

/**
 * Parse the bond block.
 * @param {string[]} lines
 * @param {number} nAtoms
 * @param {number} nBonds
 * @returns {{ a: number, b: number, molType: number }[]} 1-based atom indices
 * @throws {Error} on a malformed line or an endpoint outside the atom block
 */
function parseBondBlock(lines, nAtoms, nBonds) {
    const start = MolHeaderLines + 1 + nAtoms;
    const bonds = [];
    for (let i = 0; i < nBonds; i++) {
        const line = lines[start + i];
        if (line == null) throw new Error(`molblock bond block ends early at bond ${i + 1}`);
        const a = Number(line.slice(0, 3).trim());
        const b = Number(line.slice(3, 6).trim());
        const molType = Number(line.slice(6, 9).trim());
        if (!Number.isInteger(a) || !Number.isInteger(b) || !Number.isInteger(molType)) {
            throw new Error(`molblock bond line ${i + 1} is unparseable: "${line}"`);
        }
        if (a < 1 || a > nAtoms || b < 1 || b > nAtoms) {
            throw new Error(
                `molblock bond ${i + 1} references atom outside 1..${nAtoms}: ${a}-${b}`
            );
        }
        bonds.push({ a, b, molType });
    }
    return bonds;
}

/**
 * Parse the structural skeleton of a V2000 molblock: the counts, the atom lines and the
 * bond list. Shared by the converter and by the model-correspondence check, so a
 * molblock is parsed once per export rather than twice.
 *
 * @param {string} molBlock a V2000 molblock
 * @returns {{
 *     lines: string[], nAtoms: number, nBonds: number,
 *     parsedAtoms: object[], molBonds: {a: number, b: number, molType: number}[],
 * }}
 * @throws {Error} if the molblock is absent or malformed
 */
export function parseMolBlockSkeleton(molBlock) {
    if (!molBlock || !molBlock.trim()) throw new Error('no structure data (empty molblock)');

    const lines = molBlock.split(/\r?\n/);
    const { nAtoms, nBonds } = parseCountsLine(lines);

    const atomBlock = lines.slice(MolHeaderLines + 1, MolHeaderLines + 1 + nAtoms);
    if (atomBlock.length < nAtoms) {
        throw new Error(`molblock declares ${nAtoms} atoms but has ${atomBlock.length}`);
    }
    const parsedAtoms = getMolAtomLines(atomBlock.join('\n'));
    if (parsedAtoms.length !== nAtoms) {
        throw new Error(
            `molblock declares ${nAtoms} atoms but ${parsedAtoms.length} atom lines parsed`
        );
    }

    const molBonds = parseBondBlock(lines, nAtoms, nBonds);
    return {
        lines, nAtoms, nBonds, parsedAtoms, molBonds,
    };
}

/**
 * Convert a V2000 molblock to MolJSON.
 *
 * Aromaticity: BMaps molblocks are kekulized, so pass `aromaticBondKeys` (see
 * `aromaticBondKeysForCompound`) to get `order: 1.5`. Without it, only a molblock that
 * genuinely uses bond type 4 will produce aromatic bonds.
 *
 * @param {string} molBlock a V2000 molblock
 * @param {object} [options]
 * @param {Set<string>} [options.aromaticBondKeys] `bondKey()` values for aromatic bonds
 * @param {string[]} [options.atomNames] the model's atom names, in molblock order (see `atomIds`)
 * @returns {object} the MolJSON document
 * @throws {Error} if the molblock is absent or malformed
 */
export function molBlockToMolJson(molBlock, options={}) {
    const { aromaticBondKeys, atomNames } = options;
    const {
        lines, parsedAtoms, molBonds,
    } = parseMolBlockSkeleton(molBlock);

    const chargeOverrides = parseChargeProperties(lines);
    // 1-based index -> atom record
    const atoms = parsedAtoms.map((parsed, i) => {
        const index = i + 1;
        const formalCharge = chargeOverrides
            ? (chargeOverrides.get(index) ?? 0)
            : formalChargeFromAtomLine(parsed);
        return {
            index, element: normalizeElement(parsed.element), formalCharge, bonds: [],
        };
    });
    const atomAt = (index) => atoms[index - 1];

    // -- bonds ------------------------------------------------------------------
    const bonds = molBonds.map(({ a, b, molType }) => {
        const aromatic = aromaticBondKeys ? aromaticBondKeys.has(bondKey(a, b)) : false;
        const order = aromatic ? 1.5 : MolBondTypeToOrder[molType];
        if (order == null) {
            throw new Error(`molblock bond ${a}-${b} has unsupported bond type ${molType}`);
        }
        const bond = { a, b, order };
        atomAt(a).bonds.push(bond);
        atomAt(b).bonds.push(bond);
        return bond;
    });

    // -- aromatic N-H, counted BEFORE hydrogens are suppressed --------------------
    const isHydrogen = (atom) => atom.element === 'H';
    const aromaticNH = [];
    for (const atom of atoms) {
        if (atom.element !== 'N') continue;
        // A nitrogen IN an aromatic ring necessarily has at least TWO aromatic bonds.
        // Requiring two is a structural exclusion of the exocyclic cases this field is
        // not for -- an aniline-type NH2 conjugated to a ring, or an amide N -- rather
        // than relying on them happening not to be marked aromatic upstream.
        const aromaticBonds = atom.bonds.filter((bond) => bond.order === 1.5);
        if (aromaticBonds.length < 2) continue;
        const hcount = atom.bonds
            .map((bond) => atomAt(bond.a === atom.index ? bond.b : bond.a))
            .filter(isHydrogen).length;
        if (hcount > 0) aromaticNH.push({ atom, hcount });
    }

    // -- suppress terminal explicit hydrogens ------------------------------------
    // Only TERMINAL hydrogens (exactly one bond) are implicit in valence terms. A
    // bridging hydrogen is structural, and silently dropping it would change the
    // molecule, so it is kept as a real atom.
    const suppressed = new Set(
        atoms.filter((atom) => isHydrogen(atom) && atom.bonds.length === 1)
            .map((atom) => atom.index)
    );

    const keptAtoms = atoms.filter((atom) => !suppressed.has(atom.index));
    const keptBonds = bonds.filter(
        (bond) => !suppressed.has(bond.a) && !suppressed.has(bond.b)
    );

    // -- emit --------------------------------------------------------------------
    const ids = atomIds(keptAtoms, atomNames);
    const idOf = (atom) => ids.get(atom.index);
    // An empty sparse field is null, as in the paper's examples.
    const sparse = (entries) => (entries.length > 0 ? entries : null);
    const charges = keptAtoms
        .filter((atom) => atom.formalCharge !== 0)
        .map((atom) => ({ atom_id: idOf(atom), formal_charge: atom.formalCharge }));

    return {
        atoms: keptAtoms.map((atom) => ({ id: idOf(atom), element: atom.element })),
        bonds: keptBonds.map((bond) => ({
            source: idOf(atomAt(bond.a)),
            target: idOf(atomAt(bond.b)),
            order: bond.order,
        })),
        charges: sparse(charges),
        aromatic_n_h: sparse(
            aromaticNH.map(({ atom, hcount }) => ({ atom_id: idOf(atom), hcount }))
        ),
    };
}

/**
 * MolJSON atom ids are arbitrary strings. They are the model's atom names (`atom.atom`), so
 * an id is the name BMaps shows for that atom. Without a usable name for every exported atom
 * -- none given, one blank, or one shared by two atoms -- every id is the element and the
 * molblock's 1-based index (`N18`) instead, which stays traceable to the source block.
 *
 * @param {{ index: number, element: string }[]} atoms the exported atoms
 * @param {string[]} [atomNames] names in molblock order
 * @returns {Map<number, string>} molblock index -> id
 */
function atomIds(atoms, atomNames) {
    const names = atomNames ? atoms.map((atom) => atomNames[atom.index - 1]) : [];
    const useNames = atomNames && names.every((name) => typeof name === 'string' && name !== '')
        && new Set(names).size === names.length;
    return new Map(atoms.map((atom, i) => [
        atom.index, useNames ? names[i] : `${atom.element}${atom.index}`,
    ]));
}

/**
 * Build the set of aromatic bonds for a compound from the in-memory model, keyed by
 * 1-based molblock atom index.
 *
 * The molblock and the model describe the same atoms in the same order (both derive
 * from the same server-side atom group), but that correspondence is verified rather
 * than assumed: the element sequences and the connectivity must agree, or this returns
 * null and the caller exports without aromaticity or the model's atom names, rather than
 * emitting wrong chemistry.
 *
 * ⚠️ Reads `Bond.orderOriginal`, NOT `Bond.order` -- `updateBondOrder` rewrites `order`
 * according to the static `Bond.useKekuleBonds` display flag, so `order` is
 * display-mode dependent and unsafe here.
 *
 * @param {{ getAtoms: function(): object[] }} compound
 * @param {ReturnType<typeof parseMolBlockSkeleton>} skeleton the compound's parsed molblock
 * @returns {Set<string>|null} `bondKey()` values, or null if correspondence fails
 */
export function aromaticBondKeysForCompound(compound, skeleton) {
    const { parsedAtoms, molBonds } = skeleton || {};
    if (!parsedAtoms || !molBonds) return null;
    const modelAtoms = compound.getAtoms ? compound.getAtoms() : null;
    if (!modelAtoms || modelAtoms.length !== parsedAtoms.length) return null;

    // (1) The element sequences must agree.
    const indexOfAtom = new Map();
    for (let i = 0; i < modelAtoms.length; i++) {
        if (modelAtoms[i].elem !== parsedAtoms[i].element) return null;
        indexOfAtom.set(modelAtoms[i], i + 1);
    }

    // (2) The CONNECTIVITY must agree too. A molecule is mostly carbon, so an element
    // sequence can match under a permutation -- and a wrong mapping would attach
    // aromaticity to the wrong bonds, producing schema-valid output with wrong
    // chemistry. Requiring every model bond to exist in the molblock makes an
    // accidental correspondence implausible rather than merely unlikely.
    const molBondKeys = new Set(molBonds.map(({ a, b }) => bondKey(a, b)));
    const aromatic = new Set();
    for (const atom of modelAtoms) {
        for (const bond of atom.getBonds()) {
            const a = indexOfAtom.get(bond.atom1);
            const b = indexOfAtom.get(bond.atom2);
            // A bond to an atom outside this compound (e.g. a covalent link to the
            // protein) has no molblock index and is legitimately absent.
            if (!a || !b) continue;
            const key = bondKey(a, b);
            if (!molBondKeys.has(key)) return null;
            // ⚠️ AROMATIC ONLY (orderOriginal 8/9). DELOCALIZED (7) IS NOT AROMATIC.
            // decode.js `decodeBondType` separates them deliberately -- wire 3/4 become
            // 8/9, wire 5/6 become 7 -- and 7 covers amide, nitro and carboxylate
            // resonance. MolJSON's 1.5 means aromatic, so a delocalized bond must keep the
            // molblock's kekulized order, which is already chemically right for it.
            if (isAromaticBondOrder(bond.orderOriginal)) aromatic.add(key);
        }
    }
    return aromatic;
}

/**
 * Convert a compound to a MolJSON document.
 *
 * Checks for missing structure data explicitly -- `getMol2000()` comes back empty when the
 * server failed to deliver a compound's structure metadata (see the same guard in
 * ScoringCmds.js), and downstream callers have no null handling.
 *
 * @param {object} compound a Compound
 * @returns {{ molJson: object?, error: string? }}
 */
export function compoundToMolJsonDocument(compound) {
    const name = compound?.resSpec || 'compound';

    let molBlock;
    try {
        molBlock = compound?.getMol2000?.();
    } catch (err) {
        return { molJson: null, error: `Could not read structure data for ${name}: ${err.message}` };
    }
    if (!molBlock || !molBlock.trim()) {
        return {
            molJson: null,
            error: `${name} has no structure data, so it cannot be exported as MolJSON.`,
        };
    }

    try {
        const skeleton = parseMolBlockSkeleton(molBlock);
        const aromaticBondKeys = aromaticBondKeysForCompound(compound, skeleton) ?? undefined;
        // The keys exist only when the model atoms are the molblock's, in its order.
        const atomNames = aromaticBondKeys && compound.getAtoms().map((atom) => atom.atom);
        return {
            molJson: molBlockToMolJson(molBlock, { aromaticBondKeys, atomNames }),
            error: null,
        };
    } catch (err) {
        return { molJson: null, error: `Could not convert ${name} to MolJSON: ${err.message}` };
    }
}

/**
 * Convert one compound to MolJSON text.
 * @param {object} compound a Compound
 * @param {{indent?: number}} [options]
 * @returns {{ data: string?, error: string? }}
 */
export function compoundToMolJson(compound, options={}) {
    const { indent=2 } = options;
    const { molJson, error } = compoundToMolJsonDocument(compound);
    if (error) return { data: null, error };
    return { data: JSON.stringify(molJson, null, indent), error: null };
}

/**
 * A MolJSON document describes one molecule and the schema has no multi-molecule form, so
 * several compounds export as JSON Lines: one compact document per line.
 * @param {number} compoundCount the number of compounds requested
 * @returns {boolean}
 */
export function isMolJsonLines(compoundCount) {
    return compoundCount > 1;
}

/**
 * Convert one or more compounds to MolJSON text: one compound as a pretty-printed document,
 * several as JSON Lines (see `isMolJsonLines`).
 *
 * @param {object[]} compounds
 * @param {{indent?: number}} [options] indentation for a single document
 * @returns {{ data: string, error: string }}
 */
export function compoundsToMolJson(compounds, options={}) {
    const { indent=2 } = options;
    const documents = [];
    const errors = [];

    for (const compound of compounds) {
        const { molJson, error } = compoundToMolJsonDocument(compound);
        if (error) errors.push(error);
        else documents.push(molJson);
    }

    if (documents.length === 0) {
        return { data: '', error: errors.join('; ') || 'No compounds available for export' };
    }
    // Keyed on the count requested, as the file extension is, so a failed compound
    // doesn't turn a .jsonl file into a pretty-printed document.
    const data = isMolJsonLines(compounds.length)
        ? documents.map((doc) => `${JSON.stringify(doc)}\n`).join('')
        : JSON.stringify(documents[0], null, indent);
    return { data, error: errors.join('; ') };
}

/** Exported for tests. */
export const MolJsonInternals = {
    ChargeCodeToFormalCharge, MolJsonBondOrders, formalChargeFromAtomLine,
};
